import {
	AutoDataList,
	ContextMenu,
	ContextMenuDeleteItem,
	ContextMenuItem,
	FormattedDateTime,
	IDataListColumn,
	IconButton,
	Id,
	Trigger,
	apiIsOK,
	mapFetcherConfigToAxiosConfig,
	useEditDrawer,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { ActivityOptionDto, EmployeeOptionDto, EmployeeShowDto, EmployeeTimeListDto, ProjectOptionDto } from "apiGen";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TimeCreateDrawer, TimeFormState, TimeUpdateDrawer } from "~root/times/TimeDrawer";
import { employeeKeys } from "~shared/api/employee/employeeKeys";
import { timeKeys } from "~shared/api/times/timeKeys";
import { webserviceApi } from "~shared/api/webserviceApi";

export const EmployeeTimes: FC<{ employee: EmployeeShowDto }> = ({ employee }) => {
	const { t } = useTranslation();
	const drawerProps = useEditDrawer();
	const queryClient = useQueryClient();
	const dataListColumns: IDataListColumn<EmployeeTimeListDto>[] = useMemo(() => {
		return [
			{
				heading: t("Start"),
				valueKey: "start",
				render: ({ start }) => <FormattedDateTime dateTime={start} />,
				type: "value",
			},
			{
				heading: t("End"),
				valueKey: "end",
				render: ({ end }) => <FormattedDateTime dateTime={end} />,
				type: "value",
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuItem
							title={t("Edit time")}
							label={t("Edit time")}
							action={async () => {
								drawerProps.handleOpen(id);
							}}
							icon="edit"
							shortcut="primary"
						/>
						<ContextMenuDeleteItem
							action={async () => {
								const res = await webserviceApi.deleteTime(+id);
								if (apiIsOK(res)) {
									void queryClient.invalidateQueries({
										queryKey: timeKeys.lists(),
									});
									void queryClient.invalidateQueries({
										queryKey: employeeKeys.overview(employee.id),
									});
								}
							}}
							title={t("Delete time")}
							label={t("Delete time")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this time?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		];
	}, [drawerProps, employee.id, queryClient, t]);

	const updateTime = useCallback(
		async (id: Id, values: TimeFormState) => {
			const res = await webserviceApi.updateTime(+id, {
				start: values.start,
				end: values.end,
				employeeId: (values.employee as EmployeeOptionDto).id,
				activityId: (values.activity as ActivityOptionDto).id,
				projectId: (values.project as ProjectOptionDto).id,
			});
			if (apiIsOK(res)) {
				void queryClient.invalidateQueries({
					queryKey: timeKeys.lists(),
				});
				void queryClient.invalidateQueries({
					queryKey: employeeKeys.overview(employee.id),
				});
				return true;
			}
			return false;
		},
		[employee.id, queryClient],
	);
	const createTime = useCallback(
		async (values: TimeFormState) => {
			const res = await webserviceApi.createTime({
				start: values.start,
				end: values.end,
				employeeId: (values.employee as EmployeeOptionDto).id,
				activityId: (values.activity as ActivityOptionDto).id,
				projectId: (values.activity as ProjectOptionDto).id,
			});
			if (apiIsOK(res)) {
				void queryClient.invalidateQueries({
					queryKey: timeKeys.lists(),
				});
				void queryClient.invalidateQueries({
					queryKey: employeeKeys.overview(employee.id),
				});
				return true;
			}
			return false;
		},
		[employee.id, queryClient],
	);

	return (
		<>
			<AutoDataList
				heading={t("Times")}
				empty={t("No times available.")}
				columns={dataListColumns}
				queryKey={timeKeys.employeeList(employee.id)}
				fetcher={(config) => webserviceApi.listEmployeeTimes(employee.id, mapFetcherConfigToAxiosConfig(config))}
				headerActions={
					<>
						<Trigger
							render={(props) => (
								<TimeCreateDrawer
									open={props.open}
									onClose={props.handleClose}
									onClosed={() => undefined}
									onSubmit={createTime}
								/>
							)}
						>
							{(props) => (
								<IconButton
									{...props}
									type="button"
									color="primary"
									size="small"
									title={t("Create time")}
									icon="plus"
								/>
							)}
						</Trigger>
					</>
				}
			/>
			{drawerProps.editEntityId != null && (
				<TimeUpdateDrawer
					id={drawerProps.editEntityId}
					open={drawerProps.open}
					onClose={drawerProps.onClose}
					onClosed={drawerProps.onClosed}
					onSubmit={(values) => updateTime(drawerProps.editEntityId as number, values)}
				/>
			)}
		</>
	);
};
